import { Link, type Href } from 'expo-router';
import { Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { useState } from 'react';

import { Pill, Text } from '@/components/ui';
import { useTheme } from '@/theme';

export type DanceCardProps = {
  code: string;
  name: string;
  kicker: string;
  body: string;
  cta: string;
  href: Href;
  accent?: 'wine' | 'ember' | 'verde';
};

export function DanceCard({ code, name, kicker, body, cta, href, accent = 'wine' }: DanceCardProps) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const accentColor =
    accent === 'wine'
      ? theme.colors.accent
      : accent === 'ember'
        ? theme.colors.accentAlt
        : theme.colors.support;

  return (
    <Link href={href} asChild>
      <Pressable
        accessibilityRole="link"
        {...(Platform.OS === 'web' ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) } : {})}
        style={
          StyleSheet.flatten([
            styles.card,
            {
              backgroundColor: theme.colors.bgSoft,
              borderRadius: theme.radii.md,
              borderColor: theme.colors.rule,
              transform: hovered ? [{ translateY: -4 }] : undefined,
            },
          ]) as ViewStyle
        }
      >
        <View
          style={[
            {
              aspectRatio: 4 / 3,
              backgroundColor: accentColor,
              justifyContent: 'flex-end',
              padding: 16,
            },
            {
              backgroundImage: `repeating-linear-gradient(45deg, ${accentColor} 0, ${accentColor} 14px, ${theme.colors.fg} 14px, ${theme.colors.fg} 28px)`,
            } as any,
          ]}
        >
          <Pill variant="invert">{code}</Pill>
        </View>

        <View style={styles.body}>
          <Text variant="eyebrow" tone="fgMute">{kicker}</Text>
          <Text variant="h2" style={{ marginTop: 12 }}>
            {name}
          </Text>
          <Text variant="body" tone="fgSoft" style={{ marginTop: 16 }}>
            {body}
          </Text>
          <View style={styles.ctaRow}>
            <Text
              style={{
                color: theme.colors.accent,
                fontFamily: theme.fontFamily.bodyMedium,
                fontSize: 15,
                fontWeight: '500',
              }}
            >
              {cta} →
            </Text>
          </View>
        </View>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    minWidth: 280,
    borderWidth: 1,
    overflow: 'hidden',
  },
  body: {
    padding: 24,
    gap: 4,
  },
  ctaRow: {
    marginTop: 24,
  },
});
